import { generateSetChild, generateUpdateArrayChild } from '@/helpers/VuexHelpers'
import { service } from '@/service'
import { config } from '@/config'
import { fixTaxon, fixProductTaxons, fixProduct } from '@/service/upgradeService'
import { dayjs, dayjsServer, formatServerSimple } from '@/helpers/dayjs'

export default {
	namespaced: true,
	state: {
		brand: '',
		channel: "",
		currentTaxon: "negishi-sushi",
		categories: [],
		products: [],
		filters: [],
		productDetails: null,
		productReviews: null,
		currentProduct: null,
		loading: false,
		nextProductAvailablityChangeTime: null,
		menuMode: false,
		menuChannel: config.DEFAULT_CHANNEL,
		menuTimedAvailability: false,
	},
	actions: {
		async loadProductList({ state, commit, rootState }, { taxon, accumulatorGroup = null } = { taxon: null }) {
			commit('setLoading', { item: true })
			let basketToken = null;
			let shippingCode = null;

			if (state.menuMode) {
				state.channel = state.menuChannel ?? config.DEFAULT_CHANNEL
				shippingCode = config.MENU_SHIPPING_CODE;
			} else if (rootState.cart.serverData) {
				//TODO: this access should be less based on serverdata
				state.channel = rootState.cart.serverData.channel.code
				basketToken = rootState.cart.basketToken
			} else {
				state.channel = config.DEFAULT_CHANNEL
			}

			if (taxon) {
				commit('setActiveCategory', {item: taxon})
			}

			// don't load products, if there is no channel, to prevent wrong prices or products.
			// this mostly applies to calls before the cart has been retrieved
			if (!state.channel) {
				commit('setLoading', { item: false })
				return;
			}
			try {
				const products = await service.products({ channel: state.channel, slug: state.currentTaxon, token: basketToken, shippingCode, accumulatorGroup, deliverTime: state.menuTimedAvailability ? formatServerSimple(dayjs()) : null })

				//map products
				let productList = products?.items.reduce((p, c) => {
					if (!c.variants || !c.variants.length) {
						return p;
					}
					let item = fixProduct(c);
					p.push(item)
					return p
				}, []) || []

				commit('setProducts', {item: productList})
			}
			catch(err) {
				commit('setProducts', {item: []})
			}
			commit('setLoading', { item: false })
		},
		async loadCategories({ state, commit, rootState }, { brand, accumulatorGroup = null } = { brand: config.BRAND }) {
			let basketToken = null;
			let shippingCode = null;

			if (state.menuMode) {
				state.channel = state.menuChannel ?? config.DEFAULT_CHANNEL;
				state.brand = brand;
				shippingCode = config.MENU_SHIPPING_CODE;
			} else if (rootState.cart.serverData) {
				//TODO: this access should be less based on serverdata
				state.channel = rootState.cart.serverData.channel.code
				state.brand = rootState.cart.serverData.channel.location.brand
				basketToken = rootState.cart.basketToken
			} else {
				state.channel = config.DEFAULT_CHANNEL
				state.brand = brand
			}

			const result = await service.taxons({ brand: state.brand, channel: state.channel, accumulatorGroup, shippingCode, token: basketToken, deliverTime: state.menuTimedAvailability ? formatServerSimple(dayjs()) : null })

			let categoryList = fixTaxon(result.self).children
			let firstTaxon = categoryList[0]
			while (firstTaxon && firstTaxon.children.length > 0) {
				// search for the first cat
				firstTaxon = firstTaxon.children[0]
			}
			let firstCat = firstTaxon != null
				? firstTaxon.code
				: ''

			commit('setCategories', {item: categoryList})
			commit('setActiveCategory', {item: firstCat})
		},

		async loadNextProductAvailablityChangeTime ({ state, commit }, { accumulatorGroup = null }) {
			try {
				if (state.menuMode) {
					const result = await service.getNextProductAvailablityChangeTime({ channel: state.menuChannel, shippingCode: config.MENU_SHIPPING_CODE, accumulatorGroup })
					const time = result.nextTimeDependentChange
					if (time) {
						commit('setNextProductAvailablityChangeTime', { item: dayjsServer(time) })
					}
				}
			}
			catch {}
		},

		async readFilters({state, commit, dispatch}, { accumulatorGroup = null } = {}) {
			let data = []

			const result = await service.productTags({ channel: state.channel, accumulatorGroup })

			data = result.map(tag => ({
				id: tag.code,
				label: tag.name,
				active: false,
				group: '', // HACK: we do not have data to describe group
			}))

			commit('setFilters', {
				item: data
			})

		},

		async setFilter({state, commit}, {filterId, active}) {
			let filter = state.filters.find(f => f.id == filterId)
			if (filter) {
				filter.active = active
				commit('setFilterState', { item: filter})
			}
		},
		async getProductDetails({state, commit}, {code, accumulatorGroup = null}) {
			commit('setCurrentProduct', { item: code });
			commit('setProductDetails', { item: null });
			try {
				const result = await service.productDetails({ slug: state.currentProduct, channel: state.channel, accumulatorGroup })
				if (result) {
					const details = fixProduct(result);
					commit('setProductDetails', { item: details });
				}
			}
			catch(e) {
				//
			}
		},
		async getProductReviews({state, commit}, {code, accumulatorGroup = null}) {
			commit('setCurrentProduct', { item: code });
			try {
				const result = await service.productReviews({ slug: state.currentProduct, channel: state.channel, accumulatorGroup })
				if (result) {
					result.code = state.currentProduct;

					commit('setProductReviews', { item: result });
				}
			}
			catch(e) {
				//
			}
		},
		async sendProductReview({state, dispatch, rootState}, {code, title, rating, comment, email, accumulatorGroup = null}) {
			const result = await service.sendProductReview({ slug: code, title, rating, comment, email, token: rootState.account.token, brand: state.brand || config.BRAND, accumulatorGroup })
			if (result) {
				dispatch('getProductReviews', { code })
			}
		},
		findOverviewTaxon({state, getters}, code) {
			const taxons = getters.categories.reduce((t, cat) => {
				if (cat.code == code) {
					return getters.categories;
				}
				const child = cat.children.find(child => child.code == code);

				if (child) {
					return cat.children;
				}

				return t
			}, [])

			return taxons.find((t, i) => i == 0 && t.virtualCategory);
		},
		setMenuMode({ commit }, { menuMode, menuChannel, menuTimedAvailability } = {}) {
			commit('setMenuMode', { item: !!menuMode });
			menuChannel && commit('setMenuChannel', { item: menuChannel });
			commit('setMenuTimedAvailability', { item: menuTimedAvailability })
		},
	},
	getters: {
		productsLoading(state) {
			return state.loading;
		},
		products(state) {
			return state.products ? state.products : []
		},
		categories(state) {
			return state.categories ? state.categories : []
		},
		hasSubCategories(state, getters) {
			return getters.categories.reduce((acc, cat) => {
				return !!cat.children.length || acc;
			}, false);
		},
		filters(state) {
			return state.filters
		},
		filtersActive(state) {
			return state.filters
				.filter(f => f.active)
				.map(f => f.id)
		},
		favoriteCode(state) {
			return 'favorite'
		},
		currentTaxon(state) {
			return state.currentTaxon
		},
		currentTaxonPath(state, getters) {
			let path = []
			const buildRecur = (categories, taxon, path) => {
				const res = categories.some(cat => {
					if (cat.code == taxon) {
						path.push(cat)
						return true
					} else if (cat.children) {
						let childRes = buildRecur(cat.children, taxon, path)
						if (childRes) {
							path.push(cat)
						}
					} else {
						return false
					}
				})
				return res
			}

			// build path deep first
			buildRecur(getters.categories, state.currentTaxon, path)

			// we have to revers the path because searchin
			path = path.reverse()

			return path
		},
		currentSubCategories(state, getters) {
			let path = getters.currentTaxonPath
			if (path.length > 0) {
				return path[0].children
			}
			return []
		},
		currentCategorySiblings (state, getters) {
			let currentTaxon = getters.currentTaxon
			let currentTaxonPath = getters.currentTaxonPath
			let children = currentTaxonPath.length > 1 ? currentTaxonPath[currentTaxonPath.length - 2].children : getters.categories
			return children.filter(taxon => taxon.code != currentTaxon && taxon.virtualCategory !== true)
		},
		currentCategoryIsOverview (state, getters) {
			return getters.currentTaxonPath[getters.currentTaxonPath.length - 1]?.virtualCategory === true || false
		},
		currentCategoriesHaveOverview (state, getters) {
			if (getters.categories?.[0]?.virtualCategory) {
				return true
			}

			if (getters.hasSubCategories) {
				return getters.currentSubCategories?.[0]?.virtualCategory || false
			}

			return false
		},
		currentOverviewCategory (state, getters) {
			if (getters.currentCategoriesHaveOverview) {
				return getters.currentSubCategories[0] || getters.categories[0]
			}
		},
		currentOverviewCategoryIsTop (state, getters) {
			if (getters.currentOverviewCategory) {
				return !!getters.categories.find(cat => cat.code === getters.currentOverviewCategory.code)
			}
		},
		currentProduct(state) {
			return state.products.find(p => p.code == state.currentProduct);
		},
		currentProductDetails(state) {
			return state.productDetails;
		},
		currentProductReviews(state) {
			return {
        ...(state.productReviews || {}),
        reviews: (state.productReviews?.reviews || []).map(review => {
          return {
            ...review,
            date: dayjs(new Date(review.date)).format('L')
          }
        })
      };
		},
		menuMode(state) {
			return state.menuMode;
		},
		nextProductAvailablityChangeTime (state) {
			return state.nextProductAvailablityChangeTime
		}
	},
	mutations: {
		setActiveCategory: generateSetChild('currentTaxon'),
		setProducts: generateSetChild('products'),
		setCategories: generateSetChild('categories'),
		setFilters: generateSetChild('filters'),
		setFilterState: generateUpdateArrayChild('filters'),
		setLoading: generateSetChild('loading'),
		setProductDetails: generateSetChild('productDetails'),
		setProductReviews: generateSetChild('productReviews'),
		setCurrentProduct: generateSetChild('currentProduct'),
		setMenuMode: generateSetChild('menuMode'),
		setMenuChannel: generateSetChild('menuChannel'),
		setMenuTimedAvailability: generateSetChild('menuTimedAvailability'),
		setNextProductAvailablityChangeTime: generateSetChild('nextProductAvailablityChangeTime'),
	}
}
